import AppPageTitle from '@/components/AppPageTitle';
import React, { useEffect, useMemo, useState } from 'react';
import AppForm from '@/components/AppForm';
import PATH from '@/config/paths/PATH';
import { Col, Form, Input, Row, Switch } from 'antd';
import AppSelect from '@/components/AppSelect';
import { updateTopicAnswer } from '@/services/topic.service';
import { useMutation } from '@tanstack/react-query';
import useTopic from '@/redux/hooks/useTopic';
import { useNavigate } from 'react-router-dom';
import useTopicOptionsQuery from '@/hooks/useTopicOptionsQuery';
import useFormSubmit from '@/hooks/useFormSubmit';
import { formatDataTopicAnswer } from '@/utils';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getCharCore } from '@/services/config.service';
import { keywordInvalidValidator, keywordUnicodeValidator } from './validator';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

function TopicAnswerUpdate() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { topic, clearEditingTopicAnswerValues } = useTopic();
  const { data: topicsData } = useTopicOptionsQuery();
  const [keywordUnicode, setKeywordUnicode] = useState('');
  const { data } = usePaginatedQuery(getCharCore, ['char-core']);

  const initialValues = useMemo(() => {
    if (!topic.editTopicAnswer) return {};
    return {
      ...topic.editTopicAnswer,
      is_active: topic.editTopicAnswer?.is_active === 2 ? false : true,
    };
  }, [topic.editTopicAnswer]);

  const topicAnswerMutation = useMutation({
    mutationFn: updateTopicAnswer,
  });

  const handleOnSubmit = useFormSubmit(
    topicAnswerMutation,
    (values) => ({
      ...values,
      topic_id: String(values.topic_id),
      is_active: values.is_active ? 1 : 2,
      id: initialValues?.id.toString(),
    }),
    {
      onOk: async () => {
        await navigate(PATH.TOPIC_ANSWER.ROOT, {
          replace: true,
        });
        clearEditingTopicAnswerValues();
      },
    }
  );

  useEffect(() => {
    if (initialValues?.key_word && data?.[0]?.config_value && !keywordUnicode) {
      const unicode = formatDataTopicAnswer(data[0].config_value, initialValues.key_word);
      setKeywordUnicode(unicode);
    }
  }, [initialValues, data, keywordUnicode]);

  useEffect(() => {
    if (keywordUnicode && keywordUnicode.arrayKeyWord && form.getFieldValue('key_word')) {
      form.setFieldsValue({
        key_word_unicode: keywordUnicode.arrayKeyWord.join('/_/'),
      });
    }
  }, [keywordUnicode, form]);

  useEffect(() => {
    if (keywordUnicode && form.getFieldValue('key_word_unicode')) {
      form.validateFields(['key_word_unicode']);
    }
  }, [keywordUnicode, form]);

  const keywordUnicodeValidatorMemo = useMemo(() => {
    return (_, value) => {
      return keywordUnicodeValidator(value, keywordUnicode);
    };
  }, [keywordUnicode]);

  const keywordInvalidValidatorMemo = useMemo(() => {
    return (_, value) => {
      return keywordInvalidValidator(value, data);
    };
  }, [data]);

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <div>
      <AppPageTitle title="Update Topic Answer" />
      <AppForm
        form={form}
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        targetPath={PATH.TOPIC_ANSWER.ROOT}
        buttonProps={{
          loading: topicAnswerMutation.isPending,
          disabled: topicAnswerMutation.isPending,
        }}
        disabled={topicAnswerMutation.isPending}
      >
        <Row gutter={16}>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item
              label="Keyword"
              name="key_word"
              rules={[
                { required: true, message: 'Please enter keyword' },
                {
                  validator: keywordInvalidValidatorMemo,
                },
              ]}
              tooltip={() => <span>စျဏဋဏ</span>}
            >
              <Input
                placeholder="Enter keyword"
                onChange={() => {
                  const currentValue = form.getFieldValue('key_word');
                  if (currentValue && data?.[0]?.config_value) {
                    setKeywordUnicode(formatDataTopicAnswer(data[0].config_value, currentValue));
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              label="Keyword Unicode"
              name="key_word_unicode"
              rules={[
                { required: true, message: 'Please enter keyword unicode' },
                {
                  validator: keywordUnicodeValidatorMemo,
                },
              ]}
              tooltip={() => <span>စျ/_/ဏ/_/ဋ/_/ဏ</span>}
            >
              <Input placeholder="Enter keyword unicode" />
            </Form.Item>

            <Form.Item label="Topic" name="topic_id">
              <AppSelect
                items={topicsData?.dataRes?.map((item, index) => {
                  return {
                    value: item.id,
                    label: item.name_en.toString(),
                  };
                })}
                formItemClassName="mb-0"
                placeholder="Topic name"
              />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item
              label="Full Word Myanmar"
              name="full_word_my"
              rules={[{ required: true, message: 'Please enter full word Myanmar' }]}
            >
              <Input placeholder="Enter full word Myanmar" />
            </Form.Item>
            <Form.Item
              label="Full Word English"
              name="full_word_en"
              rules={[{ required: true, message: 'Please enter full word English' }]}
            >
              <Input placeholder="Enter full word English" />
            </Form.Item>
            <Form.Item name="is_active" valuePropName="checked" label="Active">
              <Switch title="Active" />
            </Form.Item>
          </Col>
        </Row>
      </AppForm>
    </div>
  );
}

export default TopicAnswerUpdate;
